import { useEffect, useRef, useState } from 'react';
import { Panel, PanelType } from '@fluentui/react';
import {
  Button,
  Divider,
  Dropdown,
  Field,
  Input,
  Option,
  SpinButton,
  Text,
  FluentProvider,
  makeStyles,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';

interface ICodeConventionPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  tenantOtherInfo: any;
  onFormatChanged: (
    workCodeFormat: string | undefined,
    assetCodeFormat: string | undefined,
    orderCodeFormat: string | undefined
  ) => void;
}

interface ITag {
  key: string;
  text: string;
}

const WORK_TAGS: ITag[] = [{ key: 'WID', text: 'WorkId' }];

const ASSET_TAGS: ITag[] = [
  { key: 'ZC', text: 'ZoneCode' },
  { key: 'CC', text: 'CategoryCode' },
  { key: 'ZID', text: 'ZoneId' },
  { key: 'CID', text: 'CategoryId' },
  { key: 'AID', text: 'AssetId' },
];

const ORDER_TAGS: ITag[] = [{ key: 'OID', text: 'OrderId' }];

const TAG_DISPLAY_NAMES: Record<string, string> = {
  WID: 'WorkId',
  ZC: 'ZoneCode',
  CC: 'CategoryCode',
  ZID: 'ZoneId',
  CID: 'CategoryId',
  AID: 'AssetId',
  OID: 'OrderId',
};

const TAG_SAMPLE_VALUES: Record<string, string> = {
  WID: '12',
  ZC: 'ab',
  CC: 'XY',
  ZID: '8',
  CID: '3',
  AID: '249',
  OID: '17',
};

function generatePreview(format: string): string {
  if (!format) return '';
  return format.replace(/\[([A-Z]+)(?::(\d+))?\]/g, (_, tag, digits) => {
    const sample = TAG_SAMPLE_VALUES[tag] ?? '?';
    if (digits) {
      return sample.padStart(parseInt(digits, 10), '0');
    }
    return sample;
  });
}

function formatToReadable(format: string): string {
  if (!format) return '';
  return format.replace(/\[([A-Z]+)(?::(\d+))?\]/g, (_, tag, digits) => {
    const name = TAG_DISPLAY_NAMES[tag] ?? tag;
    if (digits) return `[${name}:${digits}]`;
    return `[${name}]`;
  });
}

function validateFormat(
  format: string,
  allowedTags: string[]
): string | undefined {
  if (!format) return undefined;
  const tagsPattern = allowedTags.join('|');
  const regex = new RegExp(
    `^([^\\[\\]]*(\\[(${tagsPattern})(:\\d+)?\\]))*[^\\[\\]]*$`
  );
  if (regex.test(format)) return undefined;
  return `Invalid format. Allowed tags: ${allowedTags
    .map((t) => `[${t}]`)
    .join(', ')}. Use [TAG] or [TAG:digits].`;
}

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    flexWrap: 'wrap',
  },
  insertRow: {
    display: 'flex',
    alignItems: 'end',
    gap: '8px',
  },
  previewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  previewValue: {
    fontFamily: 'Consolas, monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '2px 8px',
    borderRadius: '4px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingTop: '8px',
  },
});

interface ICodeSectionProps {
  label: string;
  format: string;
  onFormatChange: (value: string) => void;
  tags: ITag[];
}

const CodeSection = ({
  label,
  format,
  onFormatChange,
  tags,
}: ICodeSectionProps) => {
  const styles = useStyles();
  const [selectedTag, setSelectedTag] = useState<string>(tags[0]?.key);
  const [digits, setDigits] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const validationError = validateFormat(
    format,
    tags.map((t) => t.key)
  );

  const handleInsert = () => {
    if (!selectedTag) return;
    const tagStr =
      digits > 0 ? `[${selectedTag}:${digits}]` : `[${selectedTag}]`;

    const el = inputRef.current;
    if (el) {
      const start = el.selectionStart ?? format.length;
      const end = el.selectionEnd ?? format.length;
      const newFormat =
        format.substring(0, start) + tagStr + format.substring(end);
      onFormatChange(newFormat);
      setTimeout(() => {
        const newPos = start + tagStr.length;
        el.setSelectionRange(newPos, newPos);
        el.focus();
      }, 0);
    } else {
      onFormatChange(format + tagStr);
    }
  };

  const preview = generatePreview(format);
  const readable = formatToReadable(format);

  const placeholder =
    label === 'Work Code'
      ? 'e.g. FMS-[WID:5]'
      : label === 'Order Code'
      ? 'e.g. ORD-[OID:5]'
      : 'e.g. A[AID]-Z[ZID:2]';

  return (
    <div className={styles.section}>
      <div className={styles.labelRow}>
        <Text weight="semibold" size={400}>
          {label}
        </Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {tags.map((t) => `${t.key} = ${t.text}`).join(', ')}
        </Text>
      </div>

      <Field
        validationMessage={validationError}
        validationState={validationError ? 'error' : 'none'}
      >
        <Input
          value={format}
          onChange={(_, data) => onFormatChange(data.value)}
          placeholder={placeholder}
          ref={inputRef}
          style={{ maxWidth: 400 }}
        />
      </Field>

      {readable && (
        <Text
          size={200}
          style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}
        >
          Readable: {readable}
        </Text>
      )}

      <div className={styles.insertRow}>
        <Field label="Tag">
          <Dropdown
            value={tags.find((t) => t.key === selectedTag)?.text ?? ''}
            selectedOptions={[selectedTag]}
            onOptionSelect={(_, data) =>
              setSelectedTag(data.optionValue ?? tags[0]?.key)
            }
            style={{ minWidth: 150 }}
          >
            {tags.map((t) => (
              <Option key={t.key} value={t.key}>
                {t.text}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Digits">
          <SpinButton
            min={0}
            max={20}
            value={digits}
            onChange={(_, data) => setDigits(data.value ?? 0)}
            style={{ width: 80 }}
          />
        </Field>
        <Button appearance="primary" onClick={handleInsert}>
          Insert
        </Button>
      </div>

      {preview && (
        <div className={styles.previewRow}>
          <Text size={200} weight="semibold">
            Preview:
          </Text>
          <Text size={300} className={styles.previewValue}>
            {preview}
          </Text>
        </div>
      )}
    </div>
  );
};

const CodeConventionPanel = ({
  isOpen,
  onDismiss,
  tenantOtherInfo,
  onFormatChanged,
}: ICodeConventionPanelProps) => {
  const styles = useStyles();
  const [workFormat, setWorkFormat] = useState('');
  const [assetFormat, setAssetFormat] = useState('');
  const [orderFormat, setOrderFormat] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    try {
      const info =
        typeof tenantOtherInfo === 'string'
          ? JSON.parse(tenantOtherInfo)
          : tenantOtherInfo;
      setWorkFormat(info?.['work-code-convention']?.format ?? '');
      setAssetFormat(info?.['asset-code-convention']?.format ?? '');
      setOrderFormat(info?.['order-code-convention']?.format ?? '');
    } catch {
      setWorkFormat('');
      setAssetFormat('');
      setOrderFormat('');
    }
  }, [isOpen, tenantOtherInfo]);

  const handleWorkFormatChange = (value: string) => {
    setWorkFormat(value);
    onFormatChanged(value, assetFormat, orderFormat);
  };

  const handleAssetFormatChange = (value: string) => {
    setAssetFormat(value);
    onFormatChanged(workFormat, value, orderFormat);
  };

  const handleOrderFormatChange = (value: string) => {
    setOrderFormat(value);
    onFormatChanged(workFormat, assetFormat, value);
  };

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={onDismiss}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      headerText="Code Conventions"
      styles={{ content: { paddingTop: 20 } }}
    >
      <FluentProvider theme={webLightTheme}>
        <div className={styles.container}>
          <CodeSection
            label="Work Code"
            format={workFormat}
            onFormatChange={handleWorkFormatChange}
            tags={WORK_TAGS}
          />

          <Divider />

          <CodeSection
            label="Asset Code"
            format={assetFormat}
            onFormatChange={handleAssetFormatChange}
            tags={ASSET_TAGS}
          />

          <Divider />

          <CodeSection
            label="Order Code"
            format={orderFormat}
            onFormatChange={handleOrderFormatChange}
            tags={ORDER_TAGS}
          />
        </div>
      </FluentProvider>
    </Panel>
  );
};

export default CodeConventionPanel;
